import React from "react";
import type { NonNullableField, Section } from "../../content/schema-types";
import { Button, ResponsiveImage, SectionHeader } from "../ui";

export type RelatedStoriesSectionProps = NonNullableField<Extract<Section, { type: "related-stories" }>, "content"> & {
    /**
     * Teil eines Kapitels statt eigenstaendige Sektion? (3.8.2026, umbenannt 12.8.2026)
     *
     * Auf der Startseite standen «Erfolgsgeschichte» und «Weitere
     * Erfolgsgeschichten» als zwei Bloecke — verschiedene Hintergruende, beide
     * mit voller Hoehe und eigener Ueberschrift. Entscheid Jan: ein Block, aber
     * die Hierarchie bleibt (eine Geschichte gross, vier als Kacheln). Das Flag
     * hiess deshalb zuerst `continuation`: Fortsetzung der Vorgaengersektion.
     *
     * Seit der Kapitel-Fusion (12.8.2026) ist es dasselbe wie bei den drei
     * uebrigen Teilen des Beweis-Kapitels — `nested`. Es laesst die eigene
     * Ueberschrift weg, legt Flaeche und Aussenabstand ab (beides setzt
     * `ChapterSection`) und schiebt die Kachelnamen von `h3` auf `h4`: Im
     * Kapitel steht «Erfolgsgeschichten» bereits als `h3`, und die vier Faelle
     * gehoeren darunter, nicht daneben.
     *
     * Standard `false`: die fuenf Story-Seiten (`templates/SuccessStoryPage`)
     * rendern diese Sektion eigenstaendig und muessen unveraendert bleiben.
     */
    nested?: boolean;
};

export const RelatedStoriesSection: React.FC<RelatedStoriesSectionProps> = ({
    badge,
    title,
    items,
    nested = false,
}) => {
    const ItemHeading = nested ? "h4" : "h3";

    return (
        <section data-testid="related-stories-section" className={nested ? "" : "py-24 bg-background-light"}>
            <div className="container mx-auto px-6 md:px-12 lg:px-16">
                {!nested && <SectionHeader badge={badge} title={title} className="mb-16" />}

                <div
                    className={`grid gap-8 ${
                        items.length === 1
                            ? "md:grid-cols-1 justify-items-center"
                            : items.length === 2
                              ? "md:grid-cols-2 justify-items-center"
                              : // Vier Karten (Startseite, Blocker B4): im Dreier-Raster
                                // haengt die vierte allein in der zweiten Reihe. Auf
                                // Tablet 2×2, ab Desktop alle vier nebeneinander.
                                // Die fuenf Detailseiten fuehren je drei — fuer sie
                                // aendert sich nichts.
                                items.length === 4
                                ? "md:grid-cols-2 lg:grid-cols-4"
                                : "md:grid-cols-3"
                    }`}
                >
                    {items?.map((item, i) => (
                        <div
                            key={i}
                            className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col w-full max-w-md"
                        >
                            {/* Blocker B4: auf Mobil ergaben vier Karten mit Bild und
                                Volltext 2919px — 29% der ganzen Startseite, laenger als der
                                ausformulierte Fall darueber. Das Bild bleibt deshalb bis
                                `md` weg.

                                Die Beschreibung war es urspruenglich auch; seit v0.3/F4
                                steht sie auf Mobil zweizeilig statt gar nicht (Entscheid
                                C2, siehe unten). */}
                            <div className="hidden md:flex p-8 items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100 h-64">
                                <ResponsiveImage
                                    src={item.image}
                                    alt={item.title}
                                    className="w-full h-auto rounded-lg"
                                    widths={[320, 640, 960]}
                                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 320px"
                                    objectFit="contain"
                                />
                            </div>
                            <div className="p-4 md:p-6 flex flex-col flex-grow min-h-0">
                                <ItemHeading className="text-xl md:text-2xl font-black text-primary mb-1">
                                    {item.title}
                                </ItemHeading>
                                <p className="text-sm text-primary font-semibold mb-4">{item.subtitle}</p>
                                {/* Auf Mobil zwei Zeilen, ab `md` der volle Text.

                                    Vorher stand hier `sr-only md:not-sr-only`: visuell ganz
                                    weg, im DOM erhalten. Das war ein Entscheid aus v0.2/B4 —
                                    vier Karten mit voller Beschreibung machen die Sektion auf
                                    Mobil sehr lang. Das v0.3-Feedback korrigiert ihn: «Nutzer
                                    sollten einen Satz Kontext erhalten» (Entscheid C2).

                                    `line-clamp-2` statt Kuerzung im Code: der volle Text
                                    bleibt im DOM (Screenreader, Antwortmaschinen), der
                                    Browser blendet nur den Ueberhang aus — und schneidet
                                    dabei an der Zeile, nicht mitten im Wort. */}
                                <p className="line-clamp-2 md:line-clamp-none text-black mb-4 md:mb-6 leading-relaxed md:flex-grow md:min-h-0">
                                    {item.description}
                                </p>
                                <Button href={item.href} variant="secondary" size="sm">
                                    {item.text || "Ansehen"}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default RelatedStoriesSection;
